import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { keyBy } from 'lodash'
import { useReselector, useTimeoutRef } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { UPLOAD_COPY, TRANSACTIONS_TABLE_DESCRIPTION } from './copyConstants'
import { getUserNeedsClarificationTransactionsSelector } from './transactions.selectors'
import { fetchUserTransactions } from './transactions.slice'
import { Badge, Tab, Select, Text } from '@/ui'
import { UploadButton, UploadSuccessPayload } from './Upload/Module'
import { UploadMessage } from './Upload/UploadMessage'
import TransactionsTable from './transaction-table'

import { UndoTransactionContextProvider } from './UndoTransactionContext'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'
import { isFreeTrialPromoCode } from '@/selectors/user.selectors'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { TypographyStyles } from '@/styles'
import TransactionsOnboardingCard from './TransactionsOnboardingCard'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

const TAB_INDEXES: { [key: number]: string } = {
  0: 'all',
  1: 'review',
  2: 'business',
  3: 'personal',
}

type TableViews = {
  value: number
  text: string
  type: keyof typeof TRANSACTIONS_TABLE_DESCRIPTION
}[]

export const TransactionsPanel = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)
  const needsClarificationTransactions = useReselector(
    getUserNeedsClarificationTransactionsSelector
  )
  const needsReviewLength = needsClarificationTransactions.length
  const tableViews: TableViews = [
    { value: 0, text: 'All', type: 'all' },
    {
      value: 1,
      text:
        isMobile && needsReviewLength > 0
          ? `Needs Review (${needsReviewLength})`
          : 'Needs Review',
      type: 'review',
    },
    { value: 2, text: 'Business', type: 'business' },
    { value: 3, text: 'Personal', type: 'personal' },
  ]

  const freeTrialTableViews: TableViews = [
    { value: 0, text: 'All', type: 'all' },
    { value: 1, text: 'Business', type: 'business' },
    { value: 2, text: 'Personal', type: 'personal' },
  ]
  const tableViewsMap = hasFreeTrialPromoCode
    ? keyBy(freeTrialTableViews, 'type')
    : keyBy(tableViews, 'type')
  const [fetching, setFetching] = useState(true)
  const { type } = useParams<{ type: string | undefined }>()
  const [activeIndex, setActiveIndex] = useState(() => {
    switch (type) {
      case tableViewsMap.review?.type:
        return tableViewsMap.review?.value
      case tableViewsMap.business.type:
        return tableViewsMap.business.value
      case tableViewsMap.personal.type:
        return tableViewsMap.personal.value
      default:
        return tableViewsMap.all.value
    }
  })
  const bannerTimeoutRef = useTimeoutRef()
  const [completeBanner, setCompleteBanner] = useState<{
    success: boolean
    message: string
  }>()
  const showPersonalTransactionsOnboardingStep = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enablePersonalTransactionsOnboardingStep,
    false
  )

  useEffect(() => {
    async function batchFetch() {
      await Promise.all([
        dispatch(fetchUserDocuments()),
        dispatch(fetchUserTransactions()),
      ])
      setFetching(false)
    }
    batchFetch()
  }, [dispatch, setFetching])

  const onUploadComplete = useCallback(
    ({
      success,
      data,
    }: {
      success: boolean
      data?: UploadSuccessPayload[]
    }) => {
      let message

      if (success) {
        message =
          data?.length === 1
            ? UPLOAD_COPY.SINGLE_TRANSACTION_SUCCESS_MESSAGE
            : UPLOAD_COPY.MULTIPLE_TRANSACTIONS_SUCCESS_MESSAGE
      } else {
        message = UPLOAD_COPY.SINGLE_TRANSACTION_ERROR_MESSAGE
      }
      setCompleteBanner({ success, message })

      bannerTimeoutRef.current = setTimeout(
        () => setCompleteBanner(undefined),
        5500
      )
    },
    [bannerTimeoutRef]
  )

  const updateTab = (value: string | undefined) => {
    if (value === undefined) {
      // Handle the case where the value might be undefined,
      // though with the current Select setup (not clearable, always has a value),
      // this might not be reached.
      return
    }
    const newIndex = parseInt(value, 10)
    if (isNaN(newIndex)) {
      // Handle potential parsing errors, though unlikely with current options.
      return
    }
    const newTab = TAB_INDEXES[newIndex]
    setActiveIndex(newIndex)
    navigate(`/transactions/${newTab}`, { replace: true })
  }
  const transactionTablePanes = useMemo(() => {
    if (hasFreeTrialPromoCode) {
      return [
        {
          menuItem: tableViewsMap.all.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViewsMap.all.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.business.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViewsMap.business.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.personal.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViewsMap.personal.type}
            />
          ),
        },
      ]
    } else {
      return [
        {
          menuItem: tableViewsMap.all.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViewsMap.all.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.review.text,
          ornament:
            needsReviewLength > 0 ? (
              <Badge color="red">{needsReviewLength}</Badge>
            ) : null,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              review
              tableType={tableViewsMap.review.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.business.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViewsMap.business.type}
            />
          ),
        },
        {
          menuItem: tableViewsMap.personal.text,
          render: () => (
            <TransactionsTable
              fetching={fetching}
              filterable
              tableType={tableViewsMap.personal.type}
            />
          ),
        },
      ]
    }
  }, [hasFreeTrialPromoCode, tableViewsMap, fetching, needsReviewLength])

  return (
    <UndoTransactionContextProvider>
      <div id="transactionsPanel" className={isMobile ? 'mx-2.5' : ''}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-row justify-between">
            <Text variant={TypographyStyles.H1}>Transactions</Text>
            <UploadButton onUploadComplete={onUploadComplete} />
          </div>
          {completeBanner && (
            <UploadMessage
              success={completeBanner.success}
              content={completeBanner.message}
              fadeAfterMS={5000}
            />
          )}

          <FreeTrialUnreviewedAlert />
          {showPersonalTransactionsOnboardingStep && (
            <TransactionsOnboardingCard />
          )}
        </div>

        {isMobile && (
          <>
            <Select
              value={String(activeIndex)}
              options={tableViews.map((view) => ({
                text: view.text,
                value: String(view.value),
              }))}
              onValueChange={updateTab}
            />
            <TransactionsTable
              fetching={fetching}
              filterable
              review={activeIndex === tableViewsMap.review.value}
              tableType={tableViews[activeIndex].type}
            />
          </>
        )}
        {!isMobile && (
          <Tab
            defaultActiveIndex={activeIndex}
            onTabClick={(index) => updateTab(String(index))}
            panes={transactionTablePanes}
          />
        )}
      </div>
    </UndoTransactionContextProvider>
  )
}
